import React, { Children, isValidElement, cloneElement, ComponentProps, ReactNode, useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Grid, Box, Paper, Chip, makeStyles, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, CircularProgress, Typography, IconButton, Divider } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useNavigate } from 'react-router-dom';
import * as yaml from 'js-yaml';
import { useEntity, catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityLayout,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { InfoCard, Link, EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import type { EntityNode } from '@backstage/plugin-catalog-graph';
import { CatalogGraphNode, kindPalette } from './CatalogGraphNode';
import {
  CatalogGraphEdgeDefs,
  CatalogGraphLegend,
  EdgeLabelToggle,
  useDependencyEdgeRenderer,
} from './CatalogGraphEdge';
import { GraphZoomControls, useGraphZoom } from './CatalogGraphZoom';
import { lastCatalogView } from './catalogLastView';
import {
  SystemRegistryJsonContent,
  SystemRegistryJsonTabLabel,
} from './SystemRegistryJsonContent';
import {
  readDependencyGrades,
  dependencyLabel,
  dependencyChipStyle,
} from './dependencyCriticality';
import { dependentSystemRefs, gradeFromDependent } from './dependents';
import {
  Entity,
  parseEntityRef,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

import { QuayPage, isQuayAvailable } from '@backstage-community/plugin-quay';

// ── Entity sidebar panel shown when a graph node is clicked ──────────────────
const EntitySidebarPanel = ({ entity, onClose }: { entity: Entity; onClose: () => void }) => {
  const ns = entity.metadata.namespace ?? 'default';
  const kind = entity.kind.toLowerCase();
  const name = entity.metadata.name;
  const entityPath = `/catalog/${ns}/${kind}/${name}`;
  const palette = kindPalette(kind);
  const ann = entity.metadata.annotations ?? {};
  const lbl = entity.metadata.labels ?? {};
  const spec = entity.spec as Record<string, unknown> ?? {};
  const rows: { label: string; value: string }[] = [];
  if (spec.owner) rows.push({ label: 'Owner', value: String(spec.owner).replace('group:default/', '') });
  if (ann['verint.com/team']) rows.push({ label: 'Team', value: ann['verint.com/team'] });
  if (ann['verint.com/region'] || lbl['verint.com/region']) rows.push({ label: 'Region', value: ann['verint.com/region'] || lbl['verint.com/region'] });
  if (lbl['verint.com/env'] || spec.lifecycle) rows.push({ label: 'Env', value: String(lbl['verint.com/env'] || spec.lifecycle || '') });
  if (ann['verint.com/csp']) rows.push({ label: 'CSP', value: ann['verint.com/csp'] });
  if (ann['verint.com/plane']) rows.push({ label: 'Plane', value: ann['verint.com/plane'] });
  if (spec.system) rows.push({ label: 'System', value: String(spec.system).replace('system:default/', '') });
  if (spec.domain) rows.push({ label: 'Domain', value: String(spec.domain) });
  return (
    <Paper elevation={2} style={{ height: '100%', overflowY: 'auto', padding: 16, boxSizing: 'border-box' }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
        <Box>
          <Chip
            label={entity.kind}
            size="small"
            style={{ background: palette.fill, color: palette.text, fontWeight: 600, marginBottom: 6 }}
          />
          <Typography variant="subtitle1" style={{ fontWeight: 600, lineHeight: 1.3 }}>
            {entity.metadata.title ?? entity.metadata.name}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} style={{ marginLeft: 4, flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {entity.metadata.description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          {entity.metadata.description}
        </Typography>
      )}
      <Divider style={{ marginBottom: 8 }} />
      <Table size="small">
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.label}>
              <TableCell style={{ paddingLeft: 0, color: '#888', fontWeight: 500, width: 72, border: 'none', paddingTop: 3, paddingBottom: 3 }}>{r.label}</TableCell>
              <TableCell style={{ border: 'none', paddingTop: 3, paddingBottom: 3 }}>{r.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box mt={1}>
        <Link to={entityPath} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
          <OpenInNewIcon style={{ fontSize: 14 }} /> Open in catalog
        </Link>
      </Box>
    </Paper>
  );
};

// ── Relations graph shared by every entity overview tab ──────────────────────
const GRAPH_RELATIONS = [
  RELATION_PART_OF, RELATION_HAS_PART,
  RELATION_API_CONSUMED_BY, RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API, RELATION_PROVIDES_API,
  RELATION_DEPENDENCY_OF, RELATION_DEPENDS_ON,
];

const RelationsGraphCard = ({ height = 400 }: { height?: number }) => {
  const [showLabels, setShowLabels] = useState(true);
  const renderEdge = useDependencyEdgeRenderer(showLabels);
  return (
    <Box position="relative" width="100%">
      <CatalogGraphEdgeDefs />
      <EntityCatalogGraphCard
        height={height}
        relations={GRAPH_RELATIONS}
        unidirectional={false}
        mergeRelations
        showArrowHeads
        action={<EdgeLabelToggle checked={showLabels} onChange={setShowLabels} />}
        renderEdge={renderEdge}
        renderNode={({ node }) => <CatalogGraphNode node={node as any} />}
      />
      <CatalogGraphLegend />
    </Box>
  );
};

// ── System Diagram tab card with node-click sidebar ───────────────────────────
const SystemDiagramCard = () => {
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [showLabels, setShowLabels] = useState(true);
  const renderEdge = useDependencyEdgeRenderer(showLabels);
  const { containerRef, zoomIn, zoomOut, resetZoom } = useGraphZoom();
  const allRelations = [
    RELATION_PART_OF, RELATION_HAS_PART,
    RELATION_API_CONSUMED_BY, RELATION_API_PROVIDED_BY,
    RELATION_CONSUMES_API, RELATION_PROVIDES_API,
    RELATION_DEPENDENCY_OF, RELATION_DEPENDS_ON,
  ];

  const handleNodeClick = useCallback((node: EntityNode) => {
    setSelectedEntity(prev =>
      prev?.metadata.uid === (node as any).entity?.metadata?.uid ? null : (node as any).entity ?? null
    );
  }, []);

  return (
    <Box display="flex" style={{ gap: 12, height: 720 }}>
      <Box flex={1} minWidth={0} position="relative" className="entity-graph-canvas">
        <CatalogGraphEdgeDefs />
        <div ref={containerRef}>
        <EntityCatalogGraphCard
          // Left-to-right keeps sibling dependencies in a vertical stack and
          // makes edges enter nodes on the left rather than crowding the top.
          direction={Direction.LEFT_RIGHT}
          title="System Diagram"
          height={700}
          relations={allRelations}
          // The Domain node links every sibling system through partOf/hasPart,
          // turning it into a hub whose edges cross the whole diagram.
          kinds={['System', 'Component', 'Resource', 'API']}
          unidirectional={false}
          mergeRelations
          showArrowHeads
          zoom="disabled"
          action={
            <Box display="flex" alignItems="center">
              <EdgeLabelToggle checked={showLabels} onChange={setShowLabels} />
              <GraphZoomControls zoomIn={zoomIn} zoomOut={zoomOut} resetZoom={resetZoom} />
            </Box>
          }
          onNodeClick={handleNodeClick as any}
          renderEdge={renderEdge}
          renderNode={({ node }) => (
            <CatalogGraphNode
              node={node as any}
              dimmed={selectedEntity !== null && (node as any).entity?.metadata?.uid !== selectedEntity.metadata.uid && !(node as any).focused}
            />
          )}
        />
        </div>
        <CatalogGraphLegend kinds={['System', 'Component', 'Resource', 'API']} />
      </Box>
      {selectedEntity && (
        <Box style={{ width: 280, flexShrink: 0 }}>
          <EntitySidebarPanel entity={selectedEntity} onClose={() => setSelectedEntity(null)} />
        </Box>
      )}
    </Box>
  );
};

// ── DomainHasSystemsCard ─────────────────────────────────────────────────────
// Queries the catalog API directly by spec.domain so all systems are shown
// regardless of whether the entity's hasPart relations have been stitched yet.
const DomainHasSystemsCard = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const [systems, setSystems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const domainName = entity.metadata.name;
    catalogApi
      .getEntities({ filter: { kind: 'System', 'spec.domain': domainName } })
      .then(res => setSystems(res.items))
      .catch(() => setSystems([]))
      .finally(() => setLoading(false));
  }, [entity, catalogApi]);

  return (
    <InfoCard title={`Has Systems (${loading ? '…' : systems.length})`}>
      {loading && (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      )}
      {!loading && systems.length === 0 && (
        <Typography variant="body2" color="textSecondary" style={{ padding: 16 }}>
          No systems found in this domain.
        </Typography>
      )}
      {!loading && systems.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {systems.map(s => (
                <TableRow key={s.metadata.uid} hover>
                  <TableCell>
                    <Link to={`/catalog/default/system/${s.metadata.name}`}>
                      {s.metadata.title || s.metadata.name}
                    </Link>
                  </TableCell>
                  <TableCell>{String(s.spec?.owner ?? '—').replace(/^.*\//, '')}</TableCell>
                  <TableCell>{s.metadata.description ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </InfoCard>
  );
};

// ── SystemDetailsCard ────────────────────────────────────────────────────────
// Renders Verint-specific annotations (VEN, team, region, endpoint, PCI) that
// don't appear in EntityAboutCard.
const SystemDetailsCard = () => {
  const { entity } = useEntity();
  const ann = (entity.metadata.annotations ?? {}) as Record<string, string>;
  const labels = (entity.metadata.labels ?? {}) as Record<string, string>;
  const partOf = ((entity.spec as any)?.partOf ?? []) as string[];

  const rows: { label: string; value: React.ReactNode }[] = [];

  if (ann['verint.com/ven']) {
    rows.push({ label: 'VEN', value: <code style={{ fontSize: 12 }}>{ann['verint.com/ven']}</code> });
  }
  if (partOf.length > 0) {
    rows.push({
      label: 'Parent System',
      value: (
        <>
          {partOf.map(ref => {
            const [ns, name] = ref.replace(/^system:/, '').split('/');
            return <Link key={ref} to={`/catalog/${ns || 'default'}/system/${name}`}>{name}</Link>;
          })}
        </>
      ),
    });
  }
  if (ann['verint.com/plane']) {
    rows.push({ label: 'Plane', value: ann['verint.com/plane'] });
  }
  if (ann['verint.com/csp']) {
    rows.push({ label: 'CSP', value: ann['verint.com/csp'] });
  }
  if (labels['verint.com/env']) {
    rows.push({ label: 'Environment', value: labels['verint.com/env'] });
  }
  if (ann['verint.com/endpoint-url']) {
    rows.push({
      label: 'Endpoint URL',
      value: (
        <a href={ann['verint.com/endpoint-url']} target="_blank" rel="noreferrer">
          {ann['verint.com/endpoint-url']}
        </a>
      ),
    });
  }
  if (ann['verint.com/pci-compliant'] === 'true') {
    rows.push({ label: 'PCI Compliant', value: '✅ Yes' });
  }
  if (ann['verint.com/pending'] === 'true') {
    rows.push({ label: 'Pending', value: '⚠️ Not yet usable as a dependency target' });
  }
  if (ann['verint.com/customers']) {
    const customers = ann['verint.com/customers'].split(',').filter(Boolean);
    rows.push({
      label: 'Customers',
      value: (
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          {customers.map(c => <li key={c}>{c}</li>)}
        </ul>
      ),
    });
  }
  if (ann['verint.com/metadata']) {
    try {
      const meta = JSON.parse(ann['verint.com/metadata']) as Record<string, string>;
      const entries = Object.entries(meta);
      if (entries.length > 0) {
        rows.push({
          label: 'Metadata',
          value: (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {entries.map(([k, v]) => <li key={k}><strong>{k}:</strong> {v}</li>)}
            </ul>
          ),
        });
      }
    } catch (_) {
      // ignore malformed JSON
    }
  }

  if (rows.length === 0) return null;

  return (
    <InfoCard title="System Details">
      <TableContainer>
        <Table size="small">
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.label}>
                <TableCell style={{ fontWeight: 600, width: '40%', color: '#666' }}>{row.label}</TableCell>
                <TableCell>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </InfoCard>
  );
};

// ── Shared helper for building Verint annotation rows ────────────────────────
function buildVerintRows(
  ann: Record<string, string>,
  labels: Record<string, string>,
  owner: string | undefined,
  options: { includeVen?: boolean; includeDomain?: boolean; includeEndpointUrl?: boolean; includeComponentKey?: boolean; includeOwner?: boolean } = {},
): { label: string; value: React.ReactNode }[] {
  const rows: { label: string; value: React.ReactNode }[] = [];

  if (options.includeVen && ann['verint.com/ven']) {
    rows.push({ label: 'VEN', value: <code style={{ fontSize: 12 }}>{ann['verint.com/ven']}</code> });
  }
  if (options.includeDomain && ann['verint.com/domain']) {
    rows.push({ label: 'Domain', value: ann['verint.com/domain'] });
  }
  if (options.includeOwner && owner) {
    rows.push({ label: 'Owner', value: owner.replace('group:default/', '') });
  }
  if (ann['verint.com/team']) {
    rows.push({ label: 'Team', value: ann['verint.com/team'] });
  }
  if (ann['verint.com/region']) {
    rows.push({ label: 'Region', value: ann['verint.com/region'] });
  }
  if (ann['verint.com/plane']) {
    rows.push({ label: 'Plane', value: ann['verint.com/plane'] });
  }
  if (ann['verint.com/csp']) {
    rows.push({ label: 'CSP', value: ann['verint.com/csp'] });
  }
  if (labels['verint.com/env']) {
    rows.push({ label: 'Environment', value: labels['verint.com/env'] });
  }
  if (options.includeEndpointUrl && ann['verint.com/endpoint-url']) {
    rows.push({
      label: 'Endpoint URL',
      value: (
        <a href={ann['verint.com/endpoint-url']} target="_blank" rel="noreferrer">
          {ann['verint.com/endpoint-url']}
        </a>
      ),
    });
  }
  if (options.includeComponentKey && ann['verint.com/component-key']) {
    const compKey = ann['verint.com/component-key'];
    // compKey may be "namespace/name" or just "name"; normalise to "name" for display
    const compName = compKey.includes('/') ? compKey.split('/').pop()! : compKey;
    const compNs = compKey.includes('/') ? compKey.split('/')[0] : 'default';
    rows.push({
      label: 'Component',
      value: (
        <Link to={`/catalog/${compNs}/component/${compName}`}>{compName}</Link>
      ),
    });
  }
  if (ann['verint.com/pci-compliant'] === 'true') {
    rows.push({ label: 'PCI Compliant', value: '✅ Yes' });
  }
  if (ann['verint.com/pending'] === 'true') {
    rows.push({ label: 'Pending', value: '⚠️ Not yet usable as a dependency target' });
  }
  if (ann['verint.com/metadata']) {
    try {
      const meta = JSON.parse(ann['verint.com/metadata']) as Record<string, string>;
      const entries = Object.entries(meta);
      if (entries.length > 0) {
        rows.push({
          label: 'Metadata',
          value: (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {entries.map(([k, v]) => <li key={k}><strong>{k}:</strong> {v}</li>)}
            </ul>
          ),
        });
      }
    } catch (_) {
      // ignore malformed JSON
    }
  }
  return rows;
}

function DetailsTable({ rows }: { rows: { label: string; value: React.ReactNode }[] }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.label}>
              <TableCell style={{ fontWeight: 600, width: '40%', color: '#666' }}>{row.label}</TableCell>
              <TableCell>{row.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const dependencyKindTitle: Record<string, string> = {
  api: 'APIs',
  component: 'Components',
  resource: 'Resources',
  system: 'Systems',
};

const GenericDependencyCard = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const dependsOn = useMemo(
    () => (Array.isArray((entity.spec as Record<string, unknown>)?.dependsOn)
      ? ((entity.spec as Record<string, unknown>).dependsOn as string[])
      : []),
    [entity],
  );

  // Written by the system-registry provider as { [targetEntityRef]: criticality | severity }.
  const grades = useMemo(() => readDependencyGrades(entity), [entity]);

  const [deps, setDeps] = useState<Array<{ ref: string; entity: Entity }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (dependsOn.length === 0) { setLoading(false); return; }
    Promise.all(
      dependsOn.map(async ref => {
        const { kind, namespace, name } = parseEntityRef(ref, { defaultNamespace: 'default' });
        const found = await catalogApi.getEntityByRef({ kind, namespace, name }).catch(() => undefined);
        return found ? { ref, entity: found } : undefined;
      }),
    ).then(results => {
      setDeps(results.filter((r): r is { ref: string; entity: Entity } => !!r));
      setLoading(false);
    });
  }, [dependsOn, catalogApi]);

  const grouped = deps.reduce((groups, dep) => {
    const kind = dep.entity.kind.toLowerCase();
    const list = groups.get(kind) ?? [];
    list.push(dep);
    groups.set(kind, list);
    return groups;
  }, new Map<string, Array<{ ref: string; entity: Entity }>>());

  const orderedGroups = Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b));

  return (
    <InfoCard title="Depends On">
      {loading && <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>}
      {!loading && dependsOn.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No dependencies declared for this entity.
        </Typography>
      )}
      {!loading && orderedGroups.map(([kind, entries]) => (
        <Box key={kind} mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            {dependencyKindTitle[kind] ?? `${kind.charAt(0).toUpperCase()}${kind.slice(1)}s`}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Criticality</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell>Environment</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(({ ref, entity: e }) => {
                  const ns = e.metadata.namespace ?? 'default';
                  const ann = (e.metadata.annotations ?? {}) as Record<string, string>;
                  const lbl = (e.metadata.labels ?? {}) as Record<string, string>;
                  const grade = grades[ref];
                  return (
                    <TableRow key={e.metadata.uid} hover>
                      <TableCell>
                        <Link to={`/catalog/${ns}/${kind}/${e.metadata.name}`}>
                          {e.metadata.title || e.metadata.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {grade ? (
                          <Chip
                            label={dependencyLabel(grade)}
                            size="small"
                            style={{ ...dependencyChipStyle(grade), fontWeight: 600 }}
                          />
                        ) : '—'}
                      </TableCell>
                      <TableCell>{String((e.spec as any)?.owner ?? '—').replace(/^.*\//, '')}</TableCell>
                      <TableCell>{ann['verint.com/region'] || '—'}</TableCell>
                      <TableCell>{lbl['verint.com/env'] || '—'}</TableCell>
                      <TableCell>{e.metadata.description ?? '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </InfoCard>
  );
};

// ── ComponentDetailsCard ─────────────────────────────────────────────────────
const SystemDependentsCard = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);

  const refs = useMemo(() => dependentSystemRefs(entity), [entity]);
  const [dependents, setDependents] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (refs.length === 0) {
      setDependents([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    Promise.all(
      refs.map(ref => {
        const { kind, namespace, name } = parseEntityRef(ref, { defaultNamespace: 'default' });
        return catalogApi.getEntityByRef({ kind, namespace, name }).catch(() => undefined);
      }),
    ).then(results => {
      if (cancelled) return;
      setDependents(results.filter((e): e is Entity => !!e));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [refs, catalogApi]);

  return (
    <InfoCard title={`Dependents (${loading ? '…' : dependents.length})`}>
      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}
      {!loading && dependents.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No systems depend on this system.
        </Typography>
      )}
      {!loading && dependents.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Criticality</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dependents.map(e => {
                const ns = e.metadata.namespace ?? 'default';
                const ann = (e.metadata.annotations ?? {}) as Record<string, string>;
                const lbl = (e.metadata.labels ?? {}) as Record<string, string>;
                const grade = gradeFromDependent(e, entity);
                return (
                  <TableRow key={e.metadata.uid ?? e.metadata.name} hover>
                    <TableCell>
                      <Link to={`/catalog/${ns}/system/${e.metadata.name}`}>
                        {e.metadata.title || e.metadata.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {grade ? (
                        <Chip
                          label={dependencyLabel(grade)}
                          size="small"
                          style={{ ...dependencyChipStyle(grade), fontWeight: 600 }}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>{String((e.spec as any)?.owner ?? '—').replace(/^.*\//, '')}</TableCell>
                    <TableCell>{ann['verint.com/region'] || '—'}</TableCell>
                    <TableCell>{lbl['verint.com/env'] || '—'}</TableCell>
                    <TableCell>{e.metadata.description ?? '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </InfoCard>
  );
};

const ComponentDetailsCard = () => {
  const { entity } = useEntity();
  const ann = (entity.metadata.annotations ?? {}) as Record<string, string>;
  const labels = (entity.metadata.labels ?? {}) as Record<string, string>;
  const owner = (entity.spec as Record<string, any>)?.owner as string | undefined;
  const rows = buildVerintRows(ann, labels, owner, { includeVen: true, includeOwner: true });
  if (rows.length === 0) return null;
  return (
    <InfoCard title="Component Details">
      <DetailsTable rows={rows} />
    </InfoCard>
  );
};

// ── ApiDetailsCard ───────────────────────────────────────────────────────────
const ApiDetailsCard = () => {
  const { entity } = useEntity();
  const ann = (entity.metadata.annotations ?? {}) as Record<string, string>;
  const labels = (entity.metadata.labels ?? {}) as Record<string, string>;
  const rows = buildVerintRows(ann, labels, undefined, { includeVen: true, includeEndpointUrl: true, includeComponentKey: true });
  if (rows.length === 0) return null;
  return (
    <InfoCard title="API Details">
      <DetailsTable rows={rows} />
    </InfoCard>
  );
};

// ── ResourceDetailsCard ──────────────────────────────────────────────────────
const ResourceDetailsCard = () => {
  const { entity } = useEntity();
  const ann = (entity.metadata.annotations ?? {}) as Record<string, string>;
  const labels = (entity.metadata.labels ?? {}) as Record<string, string>;
  const owner = (entity.spec as Record<string, any>)?.owner as string | undefined;
  const rows = buildVerintRows(ann, labels, owner, { includeVen: true, includeDomain: true, includeOwner: true });
  if (rows.length === 0) return null;
  return (
    <InfoCard title="Resource Details">
      <DetailsTable rows={rows} />
    </InfoCard>
  );
};

const hasSystemApis = (entity: Entity) =>
  (entity.relations ?? []).some(
    r => r.type === RELATION_HAS_PART && r.targetRef.startsWith('api:'),
  );

const hasSystemResources = (entity: Entity) =>
  (entity.relations ?? []).some(
    r => r.type === RELATION_HAS_PART && r.targetRef.startsWith('resource:'),
  );

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    {/*
      Here you can add support for different CI/CD services, for example
      using @backstage-community/plugin-github-actions as follows:
      <EntitySwitch.Case if={isGithubActionsAvailable}>
        <EntityGithubActionsContent />
      </EntitySwitch.Case>
     */}
    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
            style={{
              borderRadius: 24,
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)',
              background: 'rgba(255, 255, 255, 0.18)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#222',
              fontWeight: 700,
              letterSpacing: 1,
              transition: 'all 0.6s ease-in-out',
              padding: '10px 24px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 8px 48px 0 rgba(0, 120, 212, 0.6), 0 0 30px rgba(0, 120, 212, 0.5)';
              e.currentTarget.style.border = '1px solid rgba(0, 120, 212, 0.6)';
              e.currentTarget.style.transform = 'translateY(-4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 8px 32px 0 rgba(31, 38, 135, 0.18)';
              e.currentTarget.style.border = '1px solid rgba(255,255,255,0.3)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

// Custom About card — matches the DetailsTable style used by Component/System/etc Details cards.
const CustomAboutCard = () => {
  const { entity } = useEntity();
  const spec = entity.spec as Record<string, any> | undefined;
  const rows: { label: string; value: React.ReactNode }[] = [];

  if (entity.metadata.description) {
    rows.push({ label: 'Description', value: entity.metadata.description });
  }
  const owner = spec?.owner as string | undefined;
  if (owner) {
    const ownerName = owner.replace(/^(group|user):default\//, '');
    rows.push({ label: 'Owner', value: <Link to={`/catalog/default/group/${ownerName}`}>{ownerName}</Link> });
  }
  const system = spec?.system as string | undefined;
  if (system) {
    const sysName = system.replace(/^system:default\//, '');
    rows.push({ label: 'System', value: <Link to={`/catalog/default/system/${sysName}`}>{entity.metadata.title ? `${entity.metadata.title.split(' — ')[0]} System` : sysName}</Link> });
  }
  rows.push({ label: 'Kind', value: entity.kind });
  if (spec?.type) {
    rows.push({ label: 'Type', value: spec.type as string });
  }
  if (spec?.lifecycle) {
    rows.push({ label: 'Lifecycle', value: spec.lifecycle as string });
  }
  const tags = entity.metadata.tags ?? [];
  if (tags.length > 0) {
    rows.push({ label: 'Tags', value: tags.join(', ') });
  }

  return (
    <InfoCard title="About">
      <DetailsTable rows={rows} />
    </InfoCard>
  );
};

const useYamlStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
  pre: {
    fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: 13,
    lineHeight: 1.5,
    overflowX: 'auto',
    whiteSpace: 'pre',
    margin: 0,
    padding: theme.spacing(2),
    borderRadius: 4,
    background: '#1e1e1e',
    color: '#d4d4d4',
  },
}));

const EntityYamlContent = () => {
  const { entity } = useEntity();
  const classes = useYamlStyles();
  const yamlStr = yaml.dump(entity, { indent: 2, lineWidth: -1 });
  return (
    <Box p={2}>
      <Paper variant="outlined">
        <pre className={classes.pre}>{yamlStr}</pre>
      </Paper>
    </Box>
  );
};

const BackToCatalogButton = () => {
  const navigate = useNavigate();

  return (
    <Box mb={1} display="flex" style={{ gap: 8 }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(lastCatalogView())}
      >
        Back to Catalog
      </Button>
    </Box>
  );
};

// Wraps EntityLayout and injects BackToCatalogButton at the top of every tab's content.
// Also fixes React Router 7 tab navigation: the Overview tab's Link resolves to
// the current URL (no-op) because RoutedTabs uses `to=""` which RR7 treats as "."
// relative to the route (including the splat). The fix intercepts clicks on the
// first tab and uses `navigate('.')` with `relative: 'path'` semantics.
const OkdEntityLayout = ({ children, ...props }: ComponentProps<typeof EntityLayout>) => {
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const tabEl = (e.target as HTMLElement).closest('[role="tab"]') as HTMLElement | null;
      if (!tabEl) return;
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      if (pathParts.length < 4) return;
      const basePath = `/${pathParts.slice(0, 4).join('/')}`;

      // Determine if this is the first (Overview) tab
      const tabList = tabEl.closest('[role="tablist"]');
      const allTabs = tabList ? Array.from(tabList.querySelectorAll('[role="tab"]')) : [];
      const isFirstTab = allTabs.indexOf(tabEl) === 0;

      if (isFirstTab) {
        e.preventDefault();
        e.stopPropagation();
        navigate(basePath);
        return;
      }

      // For other tabs, resolve href and navigate
      const anchor = tabEl.tagName === 'A' ? tabEl as HTMLAnchorElement : tabEl.querySelector('a');
      const rawHref = anchor?.getAttribute('href') ?? tabEl.getAttribute('href') ?? '';
      if (!rawHref) {
        e.preventDefault();
        e.stopPropagation();
        navigate(basePath);
        return;
      }
      const absHref = rawHref.startsWith('/')
        ? rawHref
        : new URL(rawHref, window.location.href).pathname;
      if (!absHref.startsWith(basePath)) return;
      e.preventDefault();
      e.stopPropagation();
      const afterBase = absHref.slice(basePath.length).replace(/^\/|\/$/g, '');
      if (!afterBase) {
        navigate(basePath);
        return;
      }
      navigate(`${basePath}/${afterBase.split('/').pop()}`);
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [navigate]);

  const modifiedChildren = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const routeEl = child as ReturnType<typeof cloneElement> & { props: { children?: ReactNode } };
    return cloneElement(routeEl, {
      children: (
        <>
          <Box px={2} pt={2} pb={0}>
            <BackToCatalogButton />
          </Box>
          {routeEl.props.children}
        </>
      ),
    });
  });
  return <EntityLayout {...props}>{modifiedChildren}</EntityLayout>;
};

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <CustomAboutCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <RelationsGraphCard height={400} />
    </Grid>

    <Grid item md={4} xs={12}>
      <EntityHasSubcomponentsCard />
    </Grid>

  </Grid>
);

/**
 * Component and resource pages show Overview only. The other routes stay wired
 * up so they can be brought back by returning true (or a real predicate) here.
 */
const showExtraTabs = () => false;

const serviceEntityPage = (
  <OkdEntityLayout>
    <EntityLayout.Route path="/" title="Overview" tabProps={{ to: '.', relative: 'path' }}>
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6} xs={12} style={{ display: 'flex' }}>
          <Box flex={1}><CustomAboutCard /></Box>
        </Grid>
        <Grid item md={6} xs={12} style={{ display: 'flex' }}>
          <Box flex={1}><ComponentDetailsCard /></Box>
        </Grid>
        <Grid item xs={12}>
          <RelationsGraphCard height={400} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityHasSubcomponentsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD" if={showExtraTabs}>
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/image-registry"
      title="Image Registry"
      if={entity => showExtraTabs() && isQuayAvailable(entity)}
    >
      <QuayPage />
    </EntityLayout.Route>


    <EntityLayout.Route path="/api" title="API" if={showExtraTabs}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies" if={showExtraTabs}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={showExtraTabs}>
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/yaml" title="YAML" if={showExtraTabs}>
      <EntityYamlContent />
    </EntityLayout.Route>
  </OkdEntityLayout>
);

const websiteEntityPage = (
  <OkdEntityLayout>
    <EntityLayout.Route path="/" title="Overview" tabProps={{ to: '.', relative: 'path' }}>
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6} xs={12} style={{ display: 'flex' }}>
          <Box flex={1}><CustomAboutCard /></Box>
        </Grid>
        <Grid item md={6} xs={12} style={{ display: 'flex' }}>
          <Box flex={1}><ComponentDetailsCard /></Box>
        </Grid>
        <Grid item xs={12}>
          <RelationsGraphCard height={400} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityHasSubcomponentsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD" if={showExtraTabs}>
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies" if={showExtraTabs}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={showExtraTabs}>
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/yaml" title="YAML" if={showExtraTabs}>
      <EntityYamlContent />
    </EntityLayout.Route>
  </OkdEntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <OkdEntityLayout>
    <EntityLayout.Route path="/" title="Overview" tabProps={{ to: '.', relative: 'path' }}>
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/yaml" title="YAML">
      <EntityYamlContent />
    </EntityLayout.Route>
  </OkdEntityLayout>
);

// Component types without a dedicated page. Kept separate from
// `defaultEntityPage`, which is also the fallback for non-component kinds and
// keeps its full tab set.
const componentDefaultEntityPage = (
  <OkdEntityLayout>
    <EntityLayout.Route path="/" title="Overview" tabProps={{ to: '.', relative: 'path' }}>
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={showExtraTabs}>
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/yaml" title="YAML" if={showExtraTabs}>
      <EntityYamlContent />
    </EntityLayout.Route>
  </OkdEntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{componentDefaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <OkdEntityLayout>
    <EntityLayout.Route path="/" title="Overview" tabProps={{ to: '.', relative: 'path' }}>
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6} style={{ display: 'flex' }}>
          <Box flex={1}><CustomAboutCard /></Box>
        </Grid>
        <Grid item md={6} xs={12} style={{ display: 'flex' }}>
          <Box flex={1}><ApiDetailsCard /></Box>
        </Grid>
        <Grid item md={6} xs={12}>
          <RelationsGraphCard height={400} />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <GenericDependencyCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </OkdEntityLayout>
);

const userPage = (
  <OkdEntityLayout>
    <EntityLayout.Route path="/" title="Overview" tabProps={{ to: '.', relative: 'path' }}>
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </OkdEntityLayout>
);

const groupPage = (
  <OkdEntityLayout>
    <EntityLayout.Route path="/" title="Overview" tabProps={{ to: '.', relative: 'path' }}>
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </OkdEntityLayout>
);

const systemPage = (
  <OkdEntityLayout>
    <EntityLayout.Route path="/" title="Overview" tabProps={{ to: '.', relative: 'path' }}>
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        {/* Row 1: About (md=6), System Details (md=6) */}
        <Grid item md={6} xs={12} style={{ display: 'flex' }}>
          <Box flex={1}><CustomAboutCard /></Box>
        </Grid>
        <Grid item md={6} xs={12} style={{ display: 'flex' }}>
          <Box flex={1}><SystemDetailsCard /></Box>
        </Grid>
        {/* Row 2: Full-width graph — include dependency relations */}
        <Grid item xs={12}>
          <RelationsGraphCard height={450} />
        </Grid>
        {/* Row 3: Has Components */}
        <Grid item xs={12}>
          <EntityHasComponentsCard />
        </Grid>
        {/* Row 3: Has APIs | Has Resources — only shown when present */}
        <EntitySwitch>
          <EntitySwitch.Case if={hasSystemApis}>
            <Grid item xs={12}>
              <EntityHasApisCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={hasSystemResources}>
            <Grid item xs={12}>
              <EntityHasResourcesCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <SystemDiagramCard />
    </EntityLayout.Route>
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <GenericDependencyCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/dependents" title="Dependents">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <SystemDependentsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/json"
      title="JSON"
      tabProps={{ label: <SystemRegistryJsonTabLabel /> }}
    >
      <SystemRegistryJsonContent />
    </EntityLayout.Route>
  </OkdEntityLayout>
);

const domainPage = (
  <OkdEntityLayout>
    <EntityLayout.Route path="/" title="Overview" tabProps={{ to: '.', relative: 'path' }}>
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        {/* Left: compact About (md=4), Right: Has Systems takes the rest (md=8) */}
        <Grid item md={4} xs={12} style={{ display: 'flex' }}>
          <Box flex={1}><CustomAboutCard /></Box>
        </Grid>
        <Grid item md={8} xs={12}>
          <DomainHasSystemsCard />
        </Grid>
        {/* Graph spans full width below */}
        <Grid item xs={12}>
          <RelationsGraphCard height={450} />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </OkdEntityLayout>
);

const resourcePage = (
  <OkdEntityLayout>
    <EntityLayout.Route path="/" title="Overview" tabProps={{ to: '.', relative: 'path' }}>
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6} xs={12} style={{ display: 'flex' }}>
          <Box flex={1}><CustomAboutCard /></Box>
        </Grid>
        <Grid item md={6} xs={12} style={{ display: 'flex' }}>
          <Box flex={1}><ResourceDetailsCard /></Box>
        </Grid>
        <Grid item xs={12}>
          <RelationsGraphCard height={400} />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs" if={showExtraTabs}>
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/dependencies" title="Dependencies" if={showExtraTabs}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <GenericDependencyCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/yaml" title="YAML" if={showExtraTabs}>
      <EntityYamlContent />
    </EntityLayout.Route>
  </OkdEntityLayout>
);

export const entityPage = (
  <EntitySwitch>
      <EntitySwitch.Case if={isKind('component')} children={componentPage} />
      <EntitySwitch.Case if={isKind('api')} children={apiPage} />
      <EntitySwitch.Case if={isKind('group')} children={groupPage} />
      <EntitySwitch.Case if={isKind('user')} children={userPage} />
      <EntitySwitch.Case if={isKind('system')} children={systemPage} />
      <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
      <EntitySwitch.Case if={isKind('resource')} children={resourcePage} />

      <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
